import { Box, Divider, List, Text, UnstyledButton } from '@zillow/constellation-10';
import { useRef } from 'react';
import getLatestMessageDateText from '../../utils/getLatestMessageDateText';
import { FULLSTORY_EXCLUDE_CLASS_NAME, INTERACTIVE_ELEMENT_SELECTOR } from '../../constants';
import MessageContent from './MessageContent';
import UnreadIndicator from '../UnreadIndicator';
import {
  conversationItemActivateStyles,
  conversationItemStyles,
  listItemStyles,
  messageContentWrapperStyles,
} from './styles';

export type ConversationItemProps = {
  title: string;
  subtitle: string;
  latestMessage: string;
  latestMessageDateMs: string;
  isMessageOwner: boolean;
  isUnread: boolean;
  isPhoneLead: boolean;
  isArchived?: boolean;
  isSpamOrReported?: boolean;
  footer?: React.ReactNode;
  thumbnail?: React.ReactNode;
  isSelected: boolean;
  onClick: () => void;
  dataEventId?: string;
  conversationId?: string;
};

export default function ConversationItem({
  title,
  subtitle,
  latestMessage,
  latestMessageDateMs,
  isMessageOwner,
  isUnread,
  isPhoneLead,
  isArchived = false,
  isSpamOrReported = false,
  footer,
  thumbnail,
  isSelected,
  onClick,
  dataEventId,
  conversationId,
}: ConversationItemProps) {
  const footerRef = useRef<HTMLDivElement>(null);

  const handleRowClick = (event: React.MouseEvent<HTMLElement>) => {
    const control = event.target instanceof Element ? event.target.closest(INTERACTIVE_ELEMENT_SELECTOR) : null;
    // Footer controls own their clicks so consumers needn't stop propagation; passive footer content still opens the row.
    if (control && footerRef.current?.contains(control)) {
      return;
    }
    onClick();
  };

  return (
    <List.Item
      data-testid="conversation-item"
      data-eventid={dataEventId}
      data-conversation-id={conversationId}
      css={listItemStyles}
    >
      {/* Not a button, so the footer may render its own; the activation button's click bubbles here. */}
      <Box
        css={{
          ...conversationItemStyles,
          ...(isSelected && {
            backgroundColor: 'bg.action.soft.selected.default',
            borderLeftColor: 'border.express.trust.hero',
            _hover: {
              backgroundColor: 'bg.action.soft.selected.hover',
            },
          }),
        }}
        onClick={handleRowClick}
      >
        {thumbnail && <Box css={{ marginRight: 'tighter', flexShrink: 0, position: 'relative' }}>{thumbnail}</Box>}

        <Box css={{ flex: 1, minWidth: 0, paddingLeft: thumbnail ? 'tight' : 0 }}>
          <Box asChild css={conversationItemActivateStyles}>
            <UnstyledButton aria-current={isSelected ? 'true' : undefined}>
              {/* Title and Date */}
              <Box
                asChild
                css={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                }}
              >
                <span>
                  <Text
                    textStyle={isUnread || isSelected ? 'body-bold' : 'body'}
                    css={{
                      flex: 1,
                      marginRight: 'tight',
                      truncate: 'end',
                    }}
                    className={FULLSTORY_EXCLUDE_CLASS_NAME}
                    data-testid="participant-name"
                  >
                    {title}
                  </Text>

                  <Text textStyle="body-xs" css={{ color: 'text.subtle', flexShrink: 0 }}>
                    {getLatestMessageDateText(latestMessageDateMs)}
                  </Text>
                </span>
              </Box>

              {/* Subtitle */}
              {subtitle && (
                <Text
                  textStyle="body-xs"
                  css={{
                    color: 'text.subtle',
                    // A span only truncates once it is block-level.
                    display: 'block',
                    truncate: 'end',
                  }}
                  className={FULLSTORY_EXCLUDE_CLASS_NAME}
                >
                  {subtitle}
                </Text>
              )}

              {/* Message & Unread indicator */}
              <Box asChild data-testid="message-preview" css={messageContentWrapperStyles}>
                <span>
                  <MessageContent
                    isPhoneLead={isPhoneLead}
                    isMessageOwner={isMessageOwner}
                    latestMessage={latestMessage}
                    isArchived={isArchived}
                    isSpamOrReported={isSpamOrReported}
                    isUnread={isUnread}
                  />

                  {isUnread && <UnreadIndicator dataTestId="unread-badge" />}
                </span>
              </Box>
            </UnstyledButton>
          </Box>

          {/* Footer components */}
          {footer && (
            <Box
              ref={footerRef}
              css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'tighter' }}
            >
              {footer}
            </Box>
          )}
        </Box>
      </Box>

      {/* Divider is rendered outside the row for better semantics */}
      <Divider />
    </List.Item>
  );
}
